import { MouseEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { isAsyncExternalLink } from '../../store/verificationStore/functions/isAsyncExternalLink';
import { isExternalLink } from '../../store/verificationStore/functions/isExternalLink';
import { isInternalLink } from '../../store/verificationStore/functions/isInternalLink';
import { VerificationLinkProps } from './types/VerificationLinkProps';

export const VerificationLink = ({
  link,
  className,
  children,
  testId,
}: VerificationLinkProps): ReactNode => {
  const handleAnalytics = (): void => {
    if (link.onAnalytics) {
      link.onAnalytics();
    }
  };

  const handleAsyncExternalClick = async (
    event: MouseEvent,
  ): Promise<boolean> => {
    if (!isAsyncExternalLink(link)) {
      return;
    }

    event.preventDefault();

    // In order to support safari, we open a new window and capture its reference before fetching the redirect URL.
    const windowReference = window.open('', '_blank');

    const redirectURL = await link.getUrl();

    if (!redirectURL) {
      // Close the blank tab if there's no URL
      windowReference.close();
      return false;
    }

    handleAnalytics();

    windowReference.location.href = redirectURL;
    windowReference.focus();
  };

  if (isInternalLink(link)) {
    return (
      <Link
        className={className}
        data-testid={testId}
        to={link.to}
        onClick={handleAnalytics}
      >
        {children}
      </Link>
    );
  }

  if (isExternalLink(link)) {
    return (
      <a
        className={className}
        data-testid={testId}
        href={link.href}
        rel="noreferrer"
        target="_blank"
        onClick={handleAnalytics}
      >
        {children}
      </a>
    );
  }

  if (isAsyncExternalLink(link)) {
    return (
      <a
        className={className}
        data-testid={testId}
        href="#"
        onClick={handleAsyncExternalClick}
      >
        {children}
      </a>
    );
  }

  return null;
};
